import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MODULES, ROLES, ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, KeyRound, Pencil, Trash2, RefreshCw, Loader2, Building2, X } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/pages/Perfil";

const emptyModules = () => Object.fromEntries(MODULES.map((m) => [m.key, false]));

const defaultModulesForRole = (role) => {
  if (role === "master" || role === "admin") return Object.fromEntries(MODULES.map((m) => [m.key, true]));
  if (role === "agente") return Object.fromEntries(MODULES.map((m) => [m.key, ["instagram", "whatsapp"].includes(m.key)]));
  if (role === "creativo") return Object.fromEntries(MODULES.map((m) => [m.key, m.key === "motor_creativo"]));
  return emptyModules();
};

const ROLE_BADGE = {
  master: "bg-brand-emerald text-white",
  admin: "bg-brand-gold-soft text-brand-gold-dark border border-brand-gold",
  agente: "bg-stone-100 text-stone-700 border border-stone-200",
  creativo: "bg-purple-100 text-purple-700 border border-purple-200",
};

export default function Team() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [membershipsOpen, setMembershipsOpen] = useState(null); // user
  const [userMemberships, setUserMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [addCompanyId, setAddCompanyId] = useState("");

  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: "agente", modules: defaultModulesForRole("agente"),
    active: true, global_calendar_companies: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [usersResp, companiesResp] = await Promise.all([
        api.get("/team/users"),
        api.get("/companies/"),
      ]);
      setUsers(usersResp.data);
      setCompanies(companiesResp.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "", email: "", password: "",
      role: "agente", modules: defaultModulesForRole("agente"),
      active: true, global_calendar_companies: [],
    });
    setOpenDialog(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name, email: u.email, password: "",
      role: u.role, modules: { ...emptyModules(), ...u.modules },
      active: u.active,
      global_calendar_companies: u.global_calendar_companies || [],
    });
    setOpenDialog(true);
  };

  const changeRole = (role) =>
    setForm((f) => ({ ...f, role, modules: role === "master" ? defaultModulesForRole("master") : f.modules }));

  const toggleModule = (key) =>
    setForm((f) => ({ ...f, modules: { ...f.modules, [key]: !f.modules[key] } }));

  const toggleGlobalCalCompany = (companyId) =>
    setForm((f) => ({
      ...f,
      global_calendar_companies: f.global_calendar_companies.includes(companyId)
        ? f.global_calendar_companies.filter((c) => c !== companyId)
        : [...f.global_calendar_companies, companyId],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name, role: form.role, modules: form.modules, active: form.active,
        };
        if (editing.role === "admin" || form.role === "admin") {
          payload.global_calendar_companies = form.global_calendar_companies;
        }
        await api.patch(`/team/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/team/users", {
          name: form.name, email: form.email, password: form.password,
          role: form.role, modules: form.modules,
        });
        toast.success("Usuario creado");
      }
      setOpenDialog(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/team/users/${u.id}`, { active: !u.active });
      toast.success(!u.active ? "Usuario activado" : "Usuario desactivado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const doReset = async () => {
    if (!resetOpen) return;
    if ((newPassword || "").length < 6) return toast.error("Mínimo 6 caracteres");
    try {
      await api.post(`/team/users/${resetOpen.id}/reset-password`, { password: newPassword });
      toast.success("Contraseña restablecida");
      setResetOpen(null); setNewPassword("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const doDelete = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario ${u.email}? Esta acción es permanente.`)) return;
    try {
      await api.delete(`/team/users/${u.id}`);
      toast.success("Usuario eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  // -------- Memberships --------
  const openMemberships = async (u) => {
    setMembershipsOpen(u);
    setAddCompanyId("");
    await loadMemberships(u.id);
  };

  const loadMemberships = async (userId) => {
    setMembershipsLoading(true);
    try {
      const { data } = await api.get(`/team/users/${userId}/memberships`);
      setUserMemberships(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setMembershipsLoading(false);
    }
  };

  const addMembership = async () => {
    if (!addCompanyId || !membershipsOpen) return;
    const company = companies.find((c) => c.id === addCompanyId);
    if (!company) return;
    try {
      await api.post(`/team/users/${membershipsOpen.id}/memberships`, {
        company_id: addCompanyId,
        modules: company.modules,
      });
      toast.success(`Asignado a ${company.name}`);
      setAddCompanyId("");
      await loadMemberships(membershipsOpen.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const updateMembershipModule = async (m, moduleKey) => {
    const nextModules = { ...m.modules, [moduleKey]: !m.modules[moduleKey] };
    try {
      await api.post(`/team/users/${membershipsOpen.id}/memberships`, {
        company_id: m.company_id,
        modules: nextModules,
      });
      await loadMemberships(membershipsOpen.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const removeMembership = async (m) => {
    if (!window.confirm(`¿Quitar a ${membershipsOpen.email} de ${m.company_name}?`)) return;
    try {
      await api.delete(`/team/users/${membershipsOpen.id}/memberships/${m.company_id}`);
      toast.success("Membresía eliminada");
      await loadMemberships(membershipsOpen.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const availableCompaniesToAdd = companies.filter(
    (c) => !userMemberships.some((m) => m.company_id === c.id)
  );

  const showGlobalCal = editing && (editing.role === "admin" || form.role === "admin");

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="team-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold">Gestión de Equipo</h1>
          <p className="text-sm text-stone-500 mt-2">
            Administre roles, empresas asignadas y módulos por empresa de cada miembro.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-users-button">
            <RefreshCw className="h-4 w-4 mr-2" /> Recargar
          </Button>
          <Button
            onClick={openCreate}
            data-testid="create-user-button"
            className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
          >
            <Plus className="h-4 w-4 mr-2" /> Añadir Usuario
          </Button>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60">
              <TableHead>Usuario</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Módulos base</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-16">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
              </TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-16 text-stone-500">
                Sin usuarios todavía.
              </TableCell></TableRow>
            ) : (
              users.map((u) => {
                const enabled = MODULES.filter((m) => u.modules?.[m.key]);
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar user={u} size={36} />
                        <div>
                          <div className="font-medium text-brand-ink">{u.name}</div>
                          <div className="text-xs text-stone-500">{u.email}</div>
                          {u.title && <div className="text-[10px] text-stone-400 italic">{u.title}</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-full font-semibold ${ROLE_BADGE[u.role] || ""}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5 max-w-md">
                        {u.role === "master" ? (
                          <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border border-brand-emerald/20">
                            Todos los módulos
                          </Badge>
                        ) : enabled.length === 0 ? (
                          <span className="text-xs text-stone-400">—</span>
                        ) : (
                          enabled.map((m) => (
                            <Badge key={m.key} className="bg-stone-100 text-stone-700 border border-stone-200 font-medium">
                              {m.label}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.email}`} />
                        <span className={`text-xs font-medium ${u.active ? "text-brand-emerald-dark" : "text-stone-400"}`}>
                          {u.active ? "Activo" : "Inactivo"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openMemberships(u)}
                          data-testid={`memberships-user-${u.email}`} title="Empresas asignadas">
                          <Building2 className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(u)}
                          data-testid={`edit-user-${u.email}`} title="Editar">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setResetOpen(u)}
                          data-testid={`reset-password-${u.email}`} title="Restablecer contraseña">
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {u.role !== "master" && (
                          <Button size="icon" variant="ghost" onClick={() => doDelete(u)}
                            data-testid={`delete-user-${u.email}`} title="Eliminar"
                            className="text-destructive hover:bg-destructive/10">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing ? "Editar usuario" : "Añadir usuario"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Modifique rol, módulos base y permisos multi-empresa."
                : "Complete la información para crear un nuevo miembro del equipo. Se creará automáticamente su membresía en FStudy."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Nombre</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Correo electrónico</Label>
                <Input type="email" required disabled={!!editing} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
              </div>
              {!editing && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Contraseña inicial</Label>
                  <Input type="password" required minLength={6} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Rol</Label>
                <Select value={form.role} onValueChange={changeRole}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">
                  Módulos base (plantilla)
                </Label>
                {form.role === "master" && (
                  <span className="text-xs text-stone-500 italic">Master siempre tiene todos los módulos</span>
                )}
              </div>
              <p className="text-xs text-stone-400 mb-3">
                Estos módulos se copiarán a nuevas membresías. Para módulos específicos por empresa,
                use el botón <Building2 className="inline h-3 w-3" /> &quot;Empresas asignadas&quot;.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MODULES.map((m) => {
                  const disabled = form.role === "master";
                  const checked = disabled ? true : !!form.modules[m.key];
                  return (
                    <div key={m.key} className="flex items-center justify-between rounded-md border border-border bg-white px-4 py-2.5">
                      <span className="text-sm font-medium text-brand-ink">{m.label}</span>
                      <Switch checked={checked} disabled={disabled}
                        onCheckedChange={() => toggleModule(m.key)}
                        data-testid={`module-switch-${m.key}`} />
                    </div>
                  );
                })}
              </div>
            </div>

            {showGlobalCal && (
              <div className="rounded-md border border-brand-gold/40 bg-brand-gold-soft/30 p-4">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-2 block">
                  Calendario multi-empresa (empresas visibles para este Admin)
                </Label>
                <p className="text-xs text-stone-500 mb-3">
                  Seleccione las empresas cuyas publicaciones y campañas verá este administrador
                  en la vista Multi-Empresa. Solo aplica al rol Administrador.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {companies.map((c) => (
                    <label key={c.id}
                      className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 cursor-pointer"
                      data-testid={`gcc-toggle-${c.id}`}>
                      <input
                        type="checkbox"
                        checked={form.global_calendar_companies.includes(c.id)}
                        onChange={() => toggleGlobalCalCompany(c.id)}
                        className="rounded border-stone-300"
                      />
                      <span className="h-5 w-5 rounded flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: c.color || "#00A36C" }}>
                        {c.initial || c.name?.[0]}
                      </span>
                      <span className="text-sm text-brand-ink">{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {editing && (
              <div className="flex items-center gap-3 rounded-md border border-border bg-white px-4 py-3">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="user-form-active" />
                <div>
                  <div className="text-sm font-medium">Cuenta {form.active ? "Activa" : "Inactiva"}</div>
                  <div className="text-xs text-stone-500">Los usuarios inactivos no pueden iniciar sesión.</div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="user-form-submit"
                className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold">
                {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear usuario"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetOpen} onOpenChange={(v) => !v && setResetOpen(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Restablecer contraseña</DialogTitle>
            <DialogDescription>Se generará una nueva contraseña para <strong>{resetOpen?.email}</strong>.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Nueva contraseña</Label>
            <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              minLength={6} placeholder="Mínimo 6 caracteres" data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(null)}>Cancelar</Button>
            <Button onClick={doReset} data-testid="reset-password-submit"
              className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">Restablecer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Memberships Dialog */}
      <Dialog open={!!membershipsOpen} onOpenChange={(v) => !v && setMembershipsOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="memberships-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              Empresas asignadas · {membershipsOpen?.name}
            </DialogTitle>
            <DialogDescription>
              Configure los módulos habilitados por empresa. El master siempre obtiene todos los
              módulos activos de la empresa.
            </DialogDescription>
          </DialogHeader>

          {/* Add company */}
          {availableCompaniesToAdd.length > 0 && (
            <div className="flex gap-2 items-end p-3 rounded-md border border-dashed border-brand-gold/60 bg-brand-gold-soft/20">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-1 block">
                  Añadir empresa
                </Label>
                <Select value={addCompanyId} onValueChange={setAddCompanyId}>
                  <SelectTrigger data-testid="add-membership-select">
                    <SelectValue placeholder="Elegir empresa…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCompaniesToAdd.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`add-membership-option-${c.id}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={addMembership} disabled={!addCompanyId} data-testid="add-membership-submit"
                className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
                <Plus className="h-4 w-4 mr-1" /> Asignar
              </Button>
            </div>
          )}

          {membershipsLoading ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
          ) : userMemberships.length === 0 ? (
            <div className="py-10 text-center text-stone-500 text-sm">Sin empresas asignadas.</div>
          ) : (
            <div className="space-y-3">
              {userMemberships.map((m) => (
                <div key={m.company_id} className="rounded-lg border border-border bg-white p-4" data-testid={`membership-card-${m.company_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-md flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: m.company_color || "#00A36C" }}>
                        {m.company_name?.[0] || "?"}
                      </div>
                      <div>
                        <div className="font-medium text-brand-ink">{m.company_name}</div>
                        {!m.company_active && (
                          <span className="text-[10px] text-destructive">Empresa desactivada</span>
                        )}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removeMembership(m)}
                      className="text-destructive hover:bg-destructive/10"
                      data-testid={`membership-remove-${m.company_id}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {MODULES.map((mod) => {
                      const companyHas = !!(m.company_modules || {})[mod.key];
                      return (
                        <div key={mod.key}
                          className={`flex items-center justify-between rounded px-2 py-1.5 border ${companyHas ? "border-border bg-white" : "border-stone-100 bg-stone-50 opacity-50"}`}>
                          <span className="text-xs font-medium text-brand-ink">{mod.label}</span>
                          <Switch
                            checked={!!m.modules?.[mod.key]}
                            disabled={!companyHas || membershipsOpen?.role === "master"}
                            onCheckedChange={() => updateMembershipModule(m, mod.key)}
                            data-testid={`membership-module-${m.company_id}-${mod.key}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {membershipsOpen?.role === "master" && (
                    <div className="mt-2 text-[10px] text-stone-400 italic">
                      Master heredará automáticamente todos los módulos de la empresa.
                    </div>
                  )}
                  <div className="mt-3 rounded-md border border-brand-gold/30 bg-brand-gold-soft/25 p-3">
                    <MembershipAILimit
                      membership={m}
                      userId={membershipsOpen?.id}
                      onSaved={() => loadMemberships(membershipsOpen.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMembershipsOpen(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MembershipAILimit({ membership, userId, onSaved }) {
  const [unlimited, setUnlimited] = React.useState(!membership.ai_credit_limit);
  const [value, setValue] = React.useState(membership.ai_credit_limit?.value ?? 50);
  const [period, setPeriod] = React.useState(membership.ai_credit_limit?.period ?? "month");
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/team/users/${userId}/memberships`, {
        company_id: membership.company_id,
        modules: membership.modules,
        ai_credit_limit: unlimited ? null : { value: Number(value) || 0, period },
      });
      toast.success("Límite IA actualizado");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest text-brand-gold-dark font-semibold">
          Límite IA por usuario (opcional)
        </div>
        <label className="text-xs inline-flex items-center gap-1.5 text-stone-600">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => setUnlimited(e.target.checked)}
            data-testid={`membership-ai-unlimited-${membership.company_id}`}
          />
          Sin límite personal
        </label>
      </div>
      {!unlimited && (
        <div className="grid grid-cols-3 gap-2 items-end">
          <Input
            type="number" min={0} value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-8"
            data-testid={`membership-ai-value-${membership.company_id}`}
          />
          <select
            className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid={`membership-ai-period-${membership.company_id}`}
          >
            <option value="month">Mensual</option>
            <option value="week">Semanal</option>
          </select>
          <Button
            size="sm" onClick={save} disabled={saving}
            className="h-8 bg-brand-emerald text-white hover:bg-brand-emerald-dark"
            data-testid={`membership-ai-save-${membership.company_id}`}
          >
            {saving ? "…" : "Guardar"}
          </Button>
        </div>
      )}
      {unlimited && (
        <Button
          size="sm" onClick={save} disabled={saving}
          className="h-8 bg-brand-emerald text-white hover:bg-brand-emerald-dark"
          data-testid={`membership-ai-save-${membership.company_id}`}
        >
          {saving ? "…" : "Sin límite (guardar)"}
        </Button>
      )}
    </div>
  );
}
